'use client';

/**
 * LookupRenderer Component
 *
 * Renders LOOKUP and MULTI_LOOKUP field types.
 * Supports display, edit, and inline modes with async search.
 */

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha, styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getNeonStyles, type NeonAccent } from '../../../theme/palette';
import { glassEffects } from '../../../theme/shadows';
import { transitionPresets } from '../../../theme/transitions';
import type { FieldDefinition, FieldMode } from '../../../types/entityFramework';
import { transientOptions } from '../../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface LookupOption {
  id: string;
  label: string;
  [key: string]: unknown;
}

export interface LookupRendererProps {
  /** Field definition */
  field: FieldDefinition;
  /** Current value (string ID or array of string IDs) */
  value: unknown;
  /** Render mode */
  mode: FieldMode;
  /** Change handler */
  onChange?: (value: unknown) => void;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Additional class name */
  className?: string;
  /** Lookup-specific: available options */
  options?: LookupOption[];
  /** Lookup-specific: async search callback */
  onSearch?: (query: string) => Promise<LookupOption[]>;
  /** Lookup-specific: loading state */
  loading?: boolean;
}

// ============================================================================
// Style Helpers
// ============================================================================

interface AutocompleteStyleParams {
  isDark: boolean;
  neonAccent?: NeonAccent;
  hasError: boolean;
  size: 'small' | 'medium' | 'large';
}

const getAutocompleteStyles = ({ isDark, neonAccent, hasError, size }: AutocompleteStyleParams): SxProps<Theme> => {
  const neonStyles = neonAccent ? getNeonStyles(neonAccent, isDark) : null;
  const glass = isDark ? glassEffects.dark : glassEffects.light;

  const sizeStyles = {
    small: { fontSize: '0.875rem', padding: '8px 12px' },
    medium: { fontSize: '1rem', padding: '12px 16px' },
    large: { fontSize: '1.125rem', padding: '16px 20px' },
  };

  const s = sizeStyles[size];

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: glass.background,
      backdropFilter: glass.backdropBlur,
      transition: transitionPresets.input,

      '& input': {
        fontSize: s.fontSize,
        padding: s.padding,
      },

      '& fieldset': {
        borderColor: hasError ? 'error.main' : neonStyles ? neonStyles.border : glass.border,
        transition: transitionPresets.input,
      },

      '&:hover fieldset': {
        borderColor: hasError ? 'error.main' : neonStyles ? neonStyles.base : glass.borderHover,
      },

      '&.Mui-focused': {
        backgroundColor: glass.backgroundHover,

        '& fieldset': {
          borderColor: hasError ? 'error.main' : neonStyles ? neonStyles.base : 'primary.main',
          borderWidth: 2,
          boxShadow: neonStyles ? `0 0 12px ${neonStyles.glow}` : undefined,
        },
      },

      '&.Mui-disabled': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.04)',
      },
    },

    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
      fontSize: '0.875rem',

      '&.MuiInputLabel-shrink': {
        backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
        padding: '0 6px',
        marginLeft: '-4px',
      },

      '&.Mui-focused': {
        color: neonStyles ? neonStyles.base : 'primary.main',
      },
    },

    '& .MuiFormHelperText-root': {
      marginLeft: 0.5,
      marginTop: 0.75,
    },

    '& .MuiAutocomplete-tag': {
      margin: 0.25,
    },
  };
};

const getInlineAutocompleteStyles = (isDark: boolean, neonAccent?: NeonAccent): SxProps<Theme> => {
  const neonStyles = neonAccent ? getNeonStyles(neonAccent, isDark) : null;

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      backgroundColor: 'transparent',
      transition: transitionPresets.input,

      '& fieldset': {
        borderColor: 'transparent',
      },

      '&:hover fieldset': {
        borderColor: neonStyles
          ? alpha(neonStyles.border, 0.5)
          : isDark
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)',
      },

      '&.Mui-focused': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        '& fieldset': {
          borderColor: neonStyles ? neonStyles.base : 'primary.main',
          borderWidth: 1,
        },
      },
    },

    '& input': {
      padding: '4px 8px',
    },

    '& .MuiAutocomplete-tag': {
      margin: 0.125,
    },
  };
};

// ============================================================================
// Styled Components
// ============================================================================

const DisplayValue = styled(
  Typography,
  transientOptions,
)<{ $isDark: boolean }>(({ $isDark }) => ({
  color: $isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
  wordBreak: 'break-word',
}));

const DisplayChip = styled(
  Chip,
  transientOptions,
)<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const accentColor = $neonAccent ? getNeonStyles($neonAccent, $isDark).base : theme.palette.primary.main;

  return {
    backgroundColor: alpha(accentColor, $isDark ? 0.2 : 0.15),
    color: $isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
    borderColor: alpha(accentColor, 0.3),
    transition: transitionPresets.button,

    '&:hover': {
      backgroundColor: alpha(accentColor, $isDark ? 0.25 : 0.2),
    },

    '& .MuiChip-deleteIcon': {
      color: alpha(accentColor, 0.7),
      '&:hover': {
        color: accentColor,
      },
    },
  };
});

const StyledListbox = styled(
  'ul',
  transientOptions,
)<{ $isDark: boolean }>(({ $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    padding: 8,
    background: glass.background,
    backdropFilter: glass.backdropBlur,
    border: `1px solid ${glass.border}`,
    boxShadow: glass.shadow,
    borderRadius: 12,
    maxHeight: 300,
    overflow: 'auto',

    '& .MuiAutocomplete-option': {
      borderRadius: 8,
      margin: '2px 0',
      padding: '8px 12px',
      transition: transitionPresets.button,

      "&[aria-selected='true']": {
        backgroundColor: $isDark ? 'rgba(99, 91, 255, 0.2)' : 'rgba(99, 91, 255, 0.1)',
      },

      '&.Mui-focused': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
      },

      '&:hover': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.06)',
      },
    },
  };
});

// ============================================================================
// Helpers
// ============================================================================

const findOptionById = (options: LookupOption[], id: string): LookupOption | undefined => {
  return options.find((opt) => opt.id === id);
};

const normalizeValue = (
  value: unknown,
  isMulti: boolean,
  options: LookupOption[],
): LookupOption | LookupOption[] | null => {
  if (isMulti) {
    if (Array.isArray(value)) {
      return value
        .map((id) => findOptionById(options, String(id)))
        .filter((opt): opt is LookupOption => opt !== undefined);
    }
    return [];
  }
  if (value != null && value !== '') {
    return findOptionById(options, String(value)) || null;
  }
  return null;
};

const getDisplayLabel = (value: unknown, options: LookupOption[]): string | null => {
  if (value == null || value === '') return null;
  const option = findOptionById(options, String(value));
  return option?.label || String(value);
};

// ============================================================================
// Component
// ============================================================================

export const LookupRenderer: React.FC<LookupRendererProps> = ({
  field,
  value,
  mode,
  onChange,
  error,
  disabled = false,
  size = 'medium',
  neonAccent,
  className,
  options: propOptions = [],
  onSearch,
  loading: propLoading = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const isMulti = field.fieldType === 'MULTI_LOOKUP';

  // State for async search
  const [inputValue, setInputValue] = useState('');
  const [searchOptions, setSearchOptions] = useState<LookupOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Combine prop options with search results
  const options = onSearch ? searchOptions : propOptions;
  const loading = propLoading || isSearching;

  // Memoized styles
  const autocompleteStyles = useMemo(
    () =>
      getAutocompleteStyles({
        isDark,
        neonAccent,
        hasError: !!error,
        size,
      }),
    [isDark, neonAccent, error, size],
  );

  const inlineStyles = useMemo(() => getInlineAutocompleteStyles(isDark, neonAccent), [isDark, neonAccent]);

  // Debounced search effect
  useEffect(() => {
    if (!onSearch || !inputValue) {
      setSearchOptions(propOptions);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await onSearch(inputValue);
        setSearchOptions(results);
      } catch {
        setSearchOptions([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue, onSearch, propOptions]);

  // Initialize search options with prop options
  useEffect(() => {
    if (!onSearch) {
      setSearchOptions(propOptions);
    }
  }, [propOptions, onSearch]);

  const normalizedValue = normalizeValue(value, isMulti, options);

  // Handle value change
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: LookupOption | LookupOption[] | null) => {
      if (isMulti) {
        const ids = Array.isArray(newValue) ? newValue.map((opt) => opt.id) : [];
        onChange?.(ids);
      } else {
        const option = newValue as LookupOption | null;
        onChange?.(option?.id || null);
      }
    },
    [isMulti, onChange],
  );

  // Handle input change for search
  const handleInputChange = useCallback((_event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  // Render tags for multi-select — typed to match MUI v9 renderValue callback
  const renderTags = useCallback(
    // biome-ignore lint: MUI v9 renderValue has a complex union type for getItemProps
    (tagValue: LookupOption | LookupOption[], getItemProps: (args: { index: number }) => Record<string, unknown>) => {
      const items = Array.isArray(tagValue) ? tagValue : [tagValue];
      return items.map((option, index) => {
        const { key, ...tagProps } = getItemProps({ index }) as { key: string | number } & Record<string, unknown>;
        return (
          <DisplayChip
            key={key}
            label={option.label}
            size="small"
            $isDark={isDark}
            $neonAccent={neonAccent}
            {...tagProps}
          />
        );
      });
    },
    [isDark, neonAccent],
  );

  // Listbox component
  const ListboxComponent = useCallback(
    (props: React.HTMLAttributes<HTMLUListElement>) => <StyledListbox {...props} $isDark={isDark} />,
    [isDark],
  );

  // Display mode
  if (mode === 'display') {
    if (isMulti && Array.isArray(value)) {
      const values = value as string[];
      if (values.length === 0) {
        return (
          <Typography
            variant="body2"
            className={className}
            sx={{
              color: 'text.secondary',
            }}
          >
            -
          </Typography>
        );
      }
      return (
        <Box className={className} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {values.map((id) => {
            const label = getDisplayLabel(id, options);
            return (
              <DisplayChip
                key={id}
                label={label || id}
                size="small"
                variant="outlined"
                $isDark={isDark}
                $neonAccent={neonAccent}
              />
            );
          })}
        </Box>
      );
    }

    const displayLabel = getDisplayLabel(value, options);
    if (!displayLabel) {
      return (
        <Typography
          variant="body2"
          className={className}
          sx={{
            color: 'text.secondary',
          }}
        >
          -
        </Typography>
      );
    }

    return (
      <Box className={className}>
        <DisplayValue variant="body1" $isDark={isDark}>
          {displayLabel}
        </DisplayValue>
      </Box>
    );
  }

  // Inline mode
  if (mode === 'inline') {
    return (
      <Autocomplete<LookupOption, boolean, false, false>
        value={normalizedValue as LookupOption | LookupOption[] | null}
        onChange={handleChange as (event: React.SyntheticEvent, value: LookupOption | LookupOption[] | null) => void}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        options={options}
        getOptionLabel={(option) => option.label || ''}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        multiple={isMulti}
        disabled={disabled || field.disabled}
        loading={loading}
        size="small"
        fullWidth
        className={className}
        sx={inlineStyles}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={field.placeholder}
            error={!!error}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={16} />}
                    {params.slotProps.input.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderValue={renderTags}
        slotProps={{
          listbox: {
            component: ListboxComponent,
          },
        }}
      />
    );
  }

  // Edit mode
  return (
    <Autocomplete<LookupOption, boolean, false, false>
      value={normalizedValue as LookupOption | LookupOption[] | null}
      onChange={handleChange as (event: React.SyntheticEvent, value: LookupOption | LookupOption[] | null) => void}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      options={options}
      getOptionLabel={(option) => option.label || ''}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      multiple={isMulti}
      disabled={disabled || field.disabled}
      loading={loading}
      fullWidth
      className={className}
      sx={autocompleteStyles}
      renderInput={(params) => (
        <TextField
          {...params}
          label={field.label}
          placeholder={field.placeholder}
          helperText={error || field.helpText}
          error={!!error}
          required={field.required}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={20} />}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderValue={renderTags}
      slotProps={{
        listbox: {
          component: ListboxComponent,
        },
      }}
    />
  );
};

LookupRenderer.displayName = 'LookupRenderer';

export default LookupRenderer;
